import {
  Suspense,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { Outlet, useMatches, useNavigate } from 'react-router-dom'
import { Icon } from '@components/atoms/Icon'
import { Typography } from '@components/atoms/Typography'
import { PageTransition } from '@components/shell/PageTransition'
import { PageSkeleton } from '@components/shell/PageSkeleton'
import type { AppRouteConfig } from '../../../routes/config'
import type { DetailShellOutletContext } from './useDetailHeaderTitle'
import styles from './DetailShell.module.scss'

/**
 * Layout chrome for "detail-style" routes — `/task/:engagementId/:taskId`,
 * `/engagement/:engagementId`, `/engagement/:engagementId/messages`. Renders a
 * back-button header and a scrollable `<main>` landmark. No bottom nav,
 * leaves room for the future approval / messaging action bar to dock at
 * the bottom of `<main>`.
 */
export function DetailShell() {
  const matches = useMatches()
  const navigate = useNavigate()

  const handle = (matches[matches.length - 1]?.handle ?? null) as
    | AppRouteConfig
    | null
  const fallbackTitle = handle?.title ?? ''
  const routePath = handle?.path ?? ''

  // Two-tuple state so the override is automatically discarded the moment
  // the matched route path changes. Storing the path the override belongs
  // to (rather than running a reset effect) keeps state-setting out of
  // effects and avoids the cascading-renders lint rule.
  const [titleOverride, setTitleOverride] = useState<{
    path: string
    title: string | null
  }>({ path: routePath, title: null })

  const [actionsOverride, setActionsOverride] = useState<{
    path: string
    actions: ReactNode | null
  }>({ path: routePath, actions: null })

  const overrideTitle =
    titleOverride.path === routePath ? titleOverride.title : null

  const overrideActions =
    actionsOverride.path === routePath ? actionsOverride.actions : null

  const setHeaderTitle = useCallback(
    (title: string | null) => {
      setTitleOverride({ path: routePath, title })
    },
    [routePath],
  )

  const setHeaderActions = useCallback(
    (actions: ReactNode | null) => {
      setActionsOverride({ path: routePath, actions })
    },
    [routePath],
  )

  const displayTitle = overrideTitle ?? fallbackTitle

  useEffect(() => {
    document.title = fallbackTitle ? `${fallbackTitle} · ORO` : 'ORO'
  }, [fallbackTitle])

  function handleBack() {
    if (handle?.parentPath) {
      // Explicit parent — works even when the user landed on the detail
      // URL directly (e.g. shared link) and there's no back history.
      navigate(handle.parentPath)
    } else {
      navigate(-1)
    }
  }

  const outletContext: DetailShellOutletContext = {
    setHeaderTitle,
    setHeaderActions,
  }

  return (
    <div className={styles.shell}>
      <a href="#main-content" className={styles.skipLink}>
        Skip to main content
      </a>

      <header className={styles.header}>
        <button
          type="button"
          onClick={handleBack}
          className={styles.backButton}
          aria-label="Go back"
        >
          <Icon name="arrow-left" size={24} color="currentColor" />
        </button>
        <Typography
          variant="title-md"
          weight="semibold"
          color="primary"
          as="h1"
          truncate
          className={styles.title}
        >
          {displayTitle}
        </Typography>
        {overrideActions ? (
          <div className={styles.actions}>{overrideActions}</div>
        ) : null}
      </header>

      <main id="main-content" className={styles.main} tabIndex={-1}>
        <Suspense fallback={<PageSkeleton />}>
          <PageTransition>
            <Outlet context={outletContext} />
          </PageTransition>
        </Suspense>
      </main>
    </div>
  )
}
